import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { CreditCard, User, Mail, AlertCircle } from "lucide-react";
import { Button } from "./Button";
import { Card } from "./card";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { useTranslation } from "react-i18next";
import { getPlatformFeeForContract } from "../../utils/categoryFormSettingsUtils";
import { PLATFORM_CONFIG } from "../../config/platformConfig";
import { CategoryFormSettings } from "../../modules/shared/model/CategoryFormSettingsInterfaces";

const CARD_ELEMENT_OPTIONS = {
	hidePostalCode: true,
	style: {
		base: {
			fontSize: "16px",
			color: "#424770",
			"::placeholder": {
				color: "#aab7c4",
			},
		},
		invalid: {
			color: "#9e2146",
		},
	},
};

interface Props {
	milestone: any;
	onPayment: (paymentData: any) => void;
	loading?: boolean;
	categoryFormSetting?: CategoryFormSettings | null;
}

const PaymentForm: React.FC<Props> = ({ milestone, onPayment, loading = false, categoryFormSetting = null }) => {
	const { t } = useTranslation();
	const stripe = useStripe();
	const elements = useElements();
	const [formData, setFormData] = useState({
		cardholderName: "",
		email: "",
	});
	const [cardError, setCardError] = useState<string | null>(null);
	const [isProcessing, setIsProcessing] = useState(false);

	const baseAmount = Number(milestone.amount) + Number((milestone as any).trackerAmount || 0) + Number((milestone as any).emergencyCost || 0);
	const platformFeeAmount = categoryFormSetting
		? getPlatformFeeForContract(baseAmount, categoryFormSetting)
		: baseAmount * PLATFORM_CONFIG.PLATFORM_FEE_PERCENTAGE;
	const platformFeePercentage = categoryFormSetting?.platformFee ?? (PLATFORM_CONFIG.PLATFORM_FEE_PERCENTAGE * 100);
	const platformFeePercentageDisplay = Number.isInteger(platformFeePercentage)
		? `${platformFeePercentage}%`
		: `${platformFeePercentage.toFixed(2)}%`;
	const totalAmountWithFee = baseAmount + platformFeeAmount;

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		setIsProcessing(true);
		setCardError(null);

		try {
			const cardElement = elements.getElement(CardElement);
			if (!cardElement) {
				throw new Error("Card element not found");
			}

			const { error, paymentMethod } = await stripe.createPaymentMethod({
				type: "card",
				card: cardElement,
				billing_details: {
					name: formData.cardholderName,
					email: formData.email,
				},
			});

			console.log("Payment method creation result:", { error, paymentMethod });

			if (error) {
				setCardError(error.message || t("paymentForm.cardValidationFailed"));
			} else {
				setCardError(null);
				const paymentData = {
					...formData,
					paymentMethodId: paymentMethod?.id,
					milestoneId: milestone.id,
					amount: milestone.amount,
				};
				console.log("Calling onPayment with data:", paymentData);
				onPayment(paymentData);
			}
		} catch (err: any) {
			setCardError(err.message || t("paymentForm.paymentProcessingFailed"));
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<Card className="p-6">
			<div className="mb-6">
				<h3 className="text-lg font-semibold text-gray-900 mb-2">{t("paymentForm.paymentInformation")}</h3>
				<p className="text-sm text-gray-600">
					{t("paymentForm.completePaymentMessage")} <strong>{milestone.description}</strong>
				</p>
			</div>

			<form onSubmit={handleSubmit} className="space-y-4">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<label htmlFor="cardholderName" className="block text-sm font-medium text-gray-700 mb-2">
							<div className="flex items-center gap-2">
								<User className="w-4 h-4" />
								{t("paymentForm.cardholderName")}
							</div>
						</label>
						<input
							type="text"
							id="cardholderName"
							name="cardholderName"
							value={formData.cardholderName}
							onChange={handleInputChange}
							required
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
							placeholder={t("paymentForm.cardholderPlaceholder")}
						/>
					</div>

					<div>
						<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
							<div className="flex items-center gap-2">
								<Mail className="w-4 h-4" />
								{t("paymentForm.emailAddress")}
							</div>
						</label>
						<input
							type="email"
							id="email"
							name="email"
							value={formData.email}
							onChange={handleInputChange}
							required
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
							placeholder={t("paymentForm.emailPlaceholder")}
						/>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						<div className="flex items-center gap-2">
							<CreditCard className="w-4 h-4" />
							{t("paymentForm.cardDetails")}
						</div>
					</label>
					<div className="p-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
						<CardElement
							options={CARD_ELEMENT_OPTIONS}
							onChange={(event) => {
								setCardError(event.error ? event.error.message : null);
							}}
						/>
					</div>
					{cardError && (
						<div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
							<AlertCircle className="w-4 h-4" />
							{cardError}
						</div>
					)}
				</div>

			<div className="bg-gray-50 p-4 rounded-lg">
				<h4 className="font-medium text-gray-900 mb-3">{t("paymentForm.paymentSummary")}</h4>
				<div className="space-y-2">
					<div className="flex justify-between text-sm">
						<span className="text-gray-600">{t("paymentForm.milestoneAmount")}</span>
						<span className="font-medium">{getDynamicCurrencySymbol()}{Number(milestone.amount).toFixed(2)}</span>
					</div>
					{(Number((milestone as any).trackerAmount) > 0) && (
						<div className="flex justify-between text-sm">
							<span className="text-blue-600">{t("paymentForm.smartWorkTracker")}</span>
							<span className="font-medium text-blue-600">{getDynamicCurrencySymbol()}{Number((milestone as any).trackerAmount).toFixed(2)}</span>
						</div>
					)}
					{(Number((milestone as any).emergencyCost) > 0) && (
						<div className="flex justify-between text-sm">
							<span className="text-red-600">{t("paymentForm.emergencyCost")}</span>
							<span className="font-medium text-red-600">{getDynamicCurrencySymbol()}{Number((milestone as any).emergencyCost).toFixed(2)}</span>
						</div>
					)}
						<div className="flex justify-between text-sm">
							<span className="text-gray-600">{`${PLATFORM_CONFIG.PLATFORM_FEE_LABEL} (${platformFeePercentageDisplay}):`}</span>
							<span className="font-medium">{`${getDynamicCurrencySymbol()}${platformFeeAmount.toFixed(2)}`}</span>
						</div>
						<div className="flex justify-between text-sm font-semibold text-lg border-t pt-2">
							<span>{t("paymentForm.total")}</span>
							<span>{`${getDynamicCurrencySymbol()}${totalAmountWithFee.toFixed(2)}`}</span>
						</div>
					</div>
				</div>

				<Button
					type="submit"
					disabled={!stripe || loading || isProcessing}
					className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
				>
					{isProcessing ? t("paymentForm.processing") : `${t("paymentForm.pay")} ${getDynamicCurrencySymbol()}${totalAmountWithFee.toFixed(2)}`}
				</Button>
			</form>
		</Card>
	);
};

export default PaymentForm;
